/*
 * Custom PDP reviews — Element Brooklyn
 * --------------------------------------------------------------------------
 * Replaces the Okendo reviews *widget UI* with the brand's custom design,
 * while still pulling REAL review data from Okendo's public storefront API
 * (see memory/okendo-reviews-api.md). The star summary (average + count) is
 * rendered server-side in Liquid from the `reviews.*` metafields; this script
 * fills the featured quote, the review list, sorting, and "Show More".
 *
 * Config is injected by sections/pdp-reviews.liquid as a JSON <script>.
 */
(function () {
  'use strict';

  var API_BASE = 'https://api.okendo.io/v1';

  // sort value (from the dropdown) -> Okendo orderBy param
  var ORDER_BY = {
    latest: 'dateCreated desc',
    oldest: 'dateCreated asc',
    most_helpful: 'helpfulCount desc',
    highest: 'rating desc',
    lowest: 'rating asc',
    least_helpful: 'helpfulCount asc',
  };

  var SORT_OPTIONS = [
    { value: 'latest', label: 'Most Recent' },
    { value: 'oldest', label: 'Oldest' },
    { value: 'most_helpful', label: 'Most Helpful' },
    { value: 'highest', label: 'Highest Rating' },
    { value: 'lowest', label: 'Lowest Rating' },
    { value: 'least_helpful', label: 'Least Helpful' },
  ];

  // Cards shown before the first "Show More". Desktop shows 10 in the right-hand
  // list initially; mobile shows 6 (doubled from 3) to keep the page shorter.
  var INITIAL_VISIBLE = (window.matchMedia && window.matchMedia('(min-width: 768px)').matches) ? 10 : 6;
  // Okendo fetch page size — must exceed INITIAL_VISIBLE so that after the
  // featured review is filtered out of the list there are still 10 to show.
  var PAGE_SIZE = 12;

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  // Partial-fill star SVG — mirrors the harness Stars.jsx (4.3 -> 4 full + 1 @30%).
  function starsSvg(rating, size, uid) {
    var html = '<span class="pdp-stars" aria-label="' + (Math.round(rating * 10) / 10) + ' out of 5 stars">';
    var pts = '12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2';
    for (var i = 1; i <= 5; i++) {
      var fill = Math.min(1, Math.max(0, rating - (i - 1)));
      var clipId = 'rev-' + uid + '-star-' + i + '-' + Math.round(fill * 100);
      html += '<svg width="' + size + '" height="' + size + '" viewBox="0 0 24 24" aria-hidden="true" focusable="false">';
      if (fill > 0 && fill < 1) {
        html += '<defs><clipPath id="' + clipId + '"><rect x="0" y="0" width="' + (24 * fill) + '" height="24"/></clipPath></defs>';
      }
      html += '<polygon points="' + pts + '" fill="none" stroke="#201f1d" stroke-width="1.5"/>';
      if (fill > 0) {
        html += '<polygon points="' + pts + '" fill="#201f1d"' + (fill < 1 ? ' clip-path="url(#' + clipId + ')"' : '') + '/>';
      }
      html += '</svg>';
    }
    return html + '</span>';
  }

  function fmtDate(iso, long) {
    if (!iso) return '';
    var d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-US', {
      month: long ? 'long' : 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  function PdpReviews(root, cfg) {
    this.root = root;
    this.cfg = cfg;
    this.uid = cfg.productId.replace(/[^a-z0-9]/gi, '');
    this.sort = 'highest';
    this.reviews = [];
    this.nextUrl = null;
    this.expanded = false;
    this.loading = false;
    this.featured = null;

    this.els = {
      featuredWrap: root.querySelector('[data-rev-featured]'),
      ratingStars: root.querySelector('[data-rev-summary-stars]'),
      sortRoot: root.querySelector('[data-rev-sort]'),
      sortTrigger: root.querySelector('[data-rev-sort-trigger]'),
      sortLabel: root.querySelector('[data-rev-sort-label]'),
      sortCaret: root.querySelector('[data-rev-sort-caret]'),
      sortMenu: root.querySelector('[data-rev-sort-menu]'),
      list: root.querySelector('[data-rev-list]'),
      showMore: root.querySelector('[data-rev-show-more]'),
    };

    this.init();
  }

  PdpReviews.prototype.init = function () {
    var self = this;

    // Summary number + stars (server pre-rendered the number; normalize to 1dp).
    var avgEl = this.root.querySelector('[data-rev-summary-avg]');
    if (avgEl && this.cfg.average != null) avgEl.textContent = Number(this.cfg.average).toFixed(1);
    if (this.els.ratingStars && this.cfg.average != null) {
      this.els.ratingStars.innerHTML = starsSvg(this.cfg.average, 18, this.uid + '-sum');
    }

    this.buildSortMenu();

    // Sort dropdown open/close.
    if (this.els.sortTrigger) {
      this.els.sortTrigger.addEventListener('click', function (e) {
        e.stopPropagation();
        self.toggleSort();
      });
      document.addEventListener('click', function (e) {
        if (self.els.sortRoot && !self.els.sortRoot.contains(e.target)) self.closeSort();
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') self.closeSort();
      });
    }

    if (this.els.showMore) {
      this.els.showMore.addEventListener('click', function () {
        self.onShowMore();
      });
    }

    this.fetchFeatured();
    this.fetchPage(true);
  };

  PdpReviews.prototype.url = function (limit, orderBy) {
    return (
      API_BASE +
      '/stores/' +
      this.cfg.subscriberId +
      '/products/' +
      this.cfg.productId +
      '/reviews?limit=' +
      limit +
      '&orderBy=' +
      encodeURIComponent(orderBy)
    );
  };

  // One dedicated call for the left-column featured quote: a strong 5-star
  // review with a real body. Stays fixed regardless of the list sort.
  PdpReviews.prototype.fetchFeatured = function () {
    var self = this;
    // A pinned review (Ops custom.featured_review) is already server-rendered; don't override it.
    if (this.els.featuredWrap && this.els.featuredWrap.hasAttribute('data-rev-featured-pinned')) return;
    fetch(this.url(10, 'rating desc'))
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (!data || !data.reviews || !data.reviews.length) return;
        var five = data.reviews.filter(function (r) { return r.rating === 5 && r.body && r.body.length > 40; });
        self.featured = five[0] || data.reviews.filter(function (r) { return r.rating === 5; })[0] || data.reviews[0];
        self.renderFeatured();
      })
      .catch(function () {});
  };

  PdpReviews.prototype.renderFeatured = function () {
    var f = this.featured;
    var wrap = this.els.featuredWrap;
    if (!f || !wrap) return;
    var date = fmtDate(f.dateCreated, true);
    wrap.innerHTML =
      '<h6>Featured Review</h6>' +
      '<p>“' + esc(f.body) + '”</p>' +
      '<div class="review-description">- ' + esc(f.reviewer && f.reviewer.displayName) +
      (date ? ' <span class="review-date">' + esc(date) + '</span>' : '') +
      '</div>';
    wrap.hidden = false;
    // Featured is now known — re-render the list so it isn't duplicated there.
    if (this.els.list && this.reviews && this.reviews.length) this.render();
  };

  PdpReviews.prototype.buildSortMenu = function () {
    var self = this;
    if (!this.els.sortMenu) return;
    this.els.sortMenu.innerHTML = SORT_OPTIONS.map(function (o) {
      var sel = o.value === self.sort;
      return (
        '<li><button type="button" class="reviews-sort-option' + (sel ? ' selected' : '') +
        '" role="option" aria-selected="' + sel + '" data-rev-sort-value="' + o.value + '">' +
        '<span class="reviews-sort-check">' + (sel ? '✓' : '') + '</span>' + esc(o.label) +
        '</button></li>'
      );
    }).join('');
    this.els.sortMenu.querySelectorAll('[data-rev-sort-value]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        self.selectSort(btn.getAttribute('data-rev-sort-value'));
      });
    });
    this.updateSortLabel();
  };

  PdpReviews.prototype.updateSortLabel = function () {
    var opt = SORT_OPTIONS.filter(function (o) { return o.value === this.sort; }, this)[0];
    if (this.els.sortLabel && opt) this.els.sortLabel.textContent = opt.label;
  };

  PdpReviews.prototype.toggleSort = function () {
    if (this.els.sortMenu.hidden) this.openSort();
    else this.closeSort();
  };
  PdpReviews.prototype.openSort = function () {
    this.els.sortMenu.hidden = false;
    if (this.els.sortCaret) this.els.sortCaret.classList.add('open');
    this.els.sortTrigger.setAttribute('aria-expanded', 'true');
  };
  PdpReviews.prototype.closeSort = function () {
    if (!this.els.sortMenu) return;
    this.els.sortMenu.hidden = true;
    if (this.els.sortCaret) this.els.sortCaret.classList.remove('open');
    if (this.els.sortTrigger) this.els.sortTrigger.setAttribute('aria-expanded', 'false');
  };

  PdpReviews.prototype.selectSort = function (value) {
    if (value === this.sort) { this.closeSort(); return; }
    this.sort = value;
    this.reviews = [];
    this.nextUrl = null;
    this.expanded = false;
    this.buildSortMenu();
    this.closeSort();
    this.fetchPage(true);
  };

  PdpReviews.prototype.fetchPage = function (reset) {
    var self = this;
    // A sort change (reset) always wins: it supersedes whatever is in flight.
    // Only a Show More (next page) waits for an in-flight request. Each request
    // takes a sequence number and a response that is no longer the latest is
    // dropped, so a slow page from the previous sort can't land under the new
    // sort's label (or be appended to the list the sort just emptied).
    if (this.loading && !reset) return;
    this.requestSeq = (this.requestSeq || 0) + 1;
    var seq = this.requestSeq;
    this.loading = true;
    this.setLoading(true);
    var endpoint = !reset && this.nextUrl ? API_BASE + this.nextUrl : this.url(PAGE_SIZE, ORDER_BY[this.sort]);
    fetch(endpoint)
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (seq !== self.requestSeq) return;
        self.loading = false;
        self.setLoading(false);
        if (!data) return;
        if (reset) self.reviews = data.reviews || [];
        else self.reviews = self.reviews.concat(data.reviews || []);
        self.nextUrl = data.nextUrl || null;
        self.render();
      })
      .catch(function () {
        if (seq !== self.requestSeq) return;
        self.loading = false;
        self.setLoading(false);
      });
  };

  PdpReviews.prototype.onShowMore = function () {
    // Stage 1: reveal already-loaded-but-hidden cards.
    if (!this.expanded && this.reviews.length > INITIAL_VISIBLE) {
      this.expanded = true;
      this.render();
      return;
    }
    // Stage 2: fetch the next cursor page.
    this.expanded = true;
    if (this.nextUrl) this.fetchPage(false);
  };

  PdpReviews.prototype.cardHtml = function (r) {
    var name = esc(r.reviewer && r.reviewer.displayName);
    var verified = r.reviewer && r.reviewer.isVerified;
    var badge = verified ? '<span class="review-verified-badge">Verified Buyer</span>' : '';
    // Unified layout (desktop + mobile): one header row of name · stars · badge,
    // then the review body, then the date beneath it.
    return (
      '<div class="review-item">' +
        '<div class="review-item-header">' +
          '<h5 class="review-item-author">' + name + '</h5>' +
          starsSvg(r.rating, 16, this.uid + '-' + r.reviewId) +
          badge +
        '</div>' +
        '<p class="review-item-content">' + esc(r.body) + '</p>' +
        '<p class="review-item-date">' + esc(fmtDate(r.dateCreated, false)) + '</p>' +
      '</div>'
    );
  };

  // Identify the featured review so it isn't repeated in the main list.
  // Auto-pick: match by Okendo reviewId. Pinned (server-rendered): match by body text.
  PdpReviews.prototype.featuredMatcher = function () {
    if (this.featured && this.featured.reviewId != null) {
      var fid = this.featured.reviewId;
      return function (r) { return r.reviewId === fid; };
    }
    var wrap = this.els.featuredWrap;
    if (wrap && wrap.hasAttribute('data-rev-featured-pinned')) {
      var p = wrap.querySelector('p');
      var body = p ? p.textContent.replace(/^[\s"“]+|[\s"”]+$/g, '') : '';
      if (body) return function (r) { return (r.body || '').trim() === body; };
    }
    return function () { return false; };
  };

  PdpReviews.prototype.render = function () {
    var skip = this.featuredMatcher();
    var pool = this.reviews.filter(function (r) { return !skip(r); });
    var visible = this.expanded ? pool : pool.slice(0, INITIAL_VISIBLE);
    this.els.list.innerHTML = visible.map(this.cardHtml, this).join('');

    var hasHidden = !this.expanded && pool.length > INITIAL_VISIBLE;
    var hasMore = !!this.nextUrl;
    if (this.els.showMore) this.els.showMore.hidden = !(hasHidden || hasMore);
  };

  PdpReviews.prototype.setLoading = function (on) {
    var btn = this.els.showMore;
    if (!btn) return;
    btn.disabled = on;
    if (on) {
      btn.innerHTML = '<span class="review-show-more-loading"><span class="review-spinner"></span>Loading</span>';
    } else {
      btn.textContent = 'Show More';
    }
  };

  function boot() {
    document.querySelectorAll('[data-pdp-reviews]').forEach(function (root) {
      if (root.__rev) return;
      var cfgEl = root.querySelector('[data-rev-config]');
      if (!cfgEl) return;
      var cfg;
      try { cfg = JSON.parse(cfgEl.textContent); } catch (e) { return; }
      if (!cfg.subscriberId || !cfg.productId) return;
      root.__rev = new PdpReviews(root, cfg);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
